import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { DetectionBlockView } from './blocks';
import {
  resolveDetectionLogic,
  type DetectionLogicInput,
  type DetectionUnavailableReason,
} from './resolve';

/**
 * KIR-4272 — the "Detection Logic" section of the Alert Details card: why THIS
 * finding matched, as opposed to what the alert type means in general.
 *
 * Absorbs the former standalone "Why this fired" section (KIR-3781), so the card
 * never shows two competing answers to the same question.
 *
 * When no finding-specific reason was persisted the section still renders, with
 * an explicit unavailable state. It never falls back to the type-level
 * `TYPE_DISPLAY` copy — presenting that as the reason a finding fired is the bug
 * this ticket exists to fix.
 */

interface DetectionLogicProps {
  alert: DetectionLogicInput;
  /**
   * `alert_source_details` / `secret_detection_details`. The alert read path
   * strips the fields those flags gate, and several detection reasons ride on
   * them — passing the flags through is what lets the unavailable copy say
   * "turned off" instead of wrongly blaming the finding's age.
   */
  sourceDetailsAvailable?: boolean;
  secretDetailsAvailable?: boolean;
  /**
   * Route to the Edit Secret Detection Rules page (KIR-4590). Absent
   * suppresses the
   * Manage-rule action rather than rendering a dead link.
   */
  gitleaksRulesPath?: string;
  className?: string;
}

/**
 * Why the section has nothing to show. Says which of the two situations applies,
 * so an operator can tell "we don't capture this yet" from "this alert is older
 * than the field" — and in neither case mistakes it for a detection reason.
 */
const UNAVAILABLE_COPY: Record<DetectionUnavailableReason, string> = {
  'not-recorded':
    'This detection does not record a per-finding rule, so there is nothing finding-specific to show.',
  'legacy-finding':
    'No detection reason was recorded for this finding. Findings detected from now on will show one.',
  gated:
    'The detection reason for this finding is carried by a capability that is turned off for this organization, so it was not sent with the alert.',
};

export function DetectionLogic({
  alert,
  sourceDetailsAvailable,
  secretDetailsAvailable,
  gitleaksRulesPath,
  className,
}: DetectionLogicProps): ReactElement {
  const result = resolveDetectionLogic(alert, {
    ...(sourceDetailsAvailable !== undefined ? { sourceDetailsAvailable } : {}),
    ...(secretDetailsAvailable !== undefined ? { secretDetailsAvailable } : {}),
    ...(gitleaksRulesPath !== undefined ? { gitleaksRulesPath } : {}),
  });

  return (
    <section
      className={cn('mt-3', className)}
      data-testid="alert-detection-logic"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Detection Logic
      </h3>
      {/*
        One flat row sheet, not stacked sub-sections: every block renders as a
        `label | value` row on the same label column as the sibling
        "VULNERABLE DEPENDENCY" / "DETECTED ON CLIENT" panels, so the whole card
        scans as one field list. `min-w-0` lets a long verbatim value scroll
        inside its own row instead of widening the card.
      */}
      {result.available ? (
        <div className="mt-2 min-w-0">
          {result.blocks.map(block => (
            <DetectionBlockView
              key={`${block.kind}:${block.label}`}
              block={block}
            />
          ))}
        </div>
      ) : (
        <p
          className="mt-2 text-sm text-muted-foreground"
          data-testid="detection-logic-unavailable"
        >
          {UNAVAILABLE_COPY[result.because]}
        </p>
      )}
    </section>
  );
}

DetectionLogic.displayName = 'DetectionLogic';
